<template>
  <div>
    <div>
      <div class="flex flex-col mb-4">
        <p class="checklist-builder__second-stage-title">
          {{ $t('template-builder.access-authorization.title') }}
        </p>
        <p class="text-sm text-70">
          {{ $t('template-builder.access-authorization.description') }}
        </p>
      </div>
    </div>

    <div v-if="!isOnFreePlan">
      <BigField
        :class="fillableByAll ? 'checklist-builder__big-field-users--all-selected' : ''"
      >
        <template v-slot:field>
          <checkbox-field
            class="  "
            :value="fillableByAll"
            :label="$t('template-builder.access-authorization.all-users')"
            @input="toggleFillableByAll"
          />
        </template>
      </BigField>

      <div v-if="!fillableByAll">
        <choose-user-field-with-select-all
          v-if="!isOnFreePlan"
          :hide-label="true"
          :options="options"
          :disabled="isOnFreePlan"
          :value="checklistRestrictionOptions"
          :errors="[]"
          :identifier="createUserModelIdentifier.checklistBuilderUserRestriction"
          :select-all-enabled="true"
          :deselect-all-enabled="true"
          @input="setAvailabilityOptions"
        />
      </div>
    </div>

    <div v-else>
      <upgrade-ad :message="$t('upgrade-ad.cb-stage2')"
                  is-vertical
                  with-dropdown
                  :placeholder="$t('All users')" />
    </div>
  </div>
</template>

<script>
  import _ from 'lodash';
  import BigField from '@/_legacy/views/ChecklistBuilder/BigField.vue';
  import SelectControl from '@/_legacy/components/Controls/SelectControl.vue';
  import authMixin from '@/_legacy/mixins/authMixin';
  import { eventBus } from '@/_legacy/eventBus';
  import { CLICK_EVENTS, trackClick } from '@/services/activityTracker/activityTrackerHelper';
  import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
  import ChooseUserFieldWithSelectAll from '@/ui/components/user/ChooseUserFieldWithSelectAll';
  import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
  import { CheckboxField } from '@/ui/components/form';
  import { Icon } from '@/ui/components/icon/Icon';
  import { filterListToEditableGroups, filterListToEditableUsers, separateUsersAndGroups } from '@/ui/helpers';

  export default {
    components: {
      CheckboxField,
      UpgradeAd,
      BigField,
      SelectControl,
      ChooseUserFieldWithSelectAll,
      Icon,
    },

    mixins: [
      authMixin,
    ],

    props: {
      restrictions: {
        type: Object,
        default: null,
      },
      groups: {
        type: Array,
        required: true,
      },
      users: {
        type: Array,
        required: true,
      },
    },

    data() {
      return {
        freePlanModalMessage: {
          text: this.$t('This feature is not included in your free plan.'),
          advice: this.$t('Upgrade now to hide this checklist from specific users, groups or entity items'),
        },
        checklistRestrictions: {
          groups: [],
          users: [],
        },
        createUserModelIdentifier: CreateUserModelIdentifier,
        unWatchOptions: null,
        mergedOptions: [],
      };
    },

    computed: {
      fillableByAll() {
        return this.checklist.fillableByAll;
      },

      options() {
        const users = this.users.map(user => ({ ...user, value: `user_${user.value}` }));
        const groups = this.groups.map(group => ({ ...group, value: `group_${group.value}` }));

        const finalOptions = [...users, ...groups];

        this.mergedOptions = finalOptions;

        return finalOptions;
      },

      everybody() {
        return _.isEmpty(this.checklistRestrictions.groups)
          && _.isEmpty(this.checklistRestrictions.users);
      },

      checklistRestrictionOptions() {
        const { users, groups } = this.checklistRestrictions;

        return this.getOptions(users, groups);
      },

      checklist() {
        return this.$store.getters['checklist_builder/checklist'];
      },
    },

    watch: {
      fillableByAll: {
        deep: false,
        handler(fillableByAll) {
          if (!fillableByAll) {
            trackClick(CLICK_EVENTS.RESTRICT_AVAILABILITY);
          }
        },
      }
    },

    mounted() {
      this.checklistRestrictions = this.restrictions || {
        groups: [],
        users: [],
      };
    },

    methods: {
      toggleFillableByAll() {
        this.$store.commit('checklist_builder/setFillableByAll', !this.fillableByAll);
      },

      onUserCreated({ response, identifier }) {
        if (CreateUserModelIdentifier.checklistBuilderUserRestriction !== identifier) {
          return;
        }

        this.setAvailabilityOptions(options.value.push(`user_${response.id}`));
      },

      selectAllUsers() {
        const options = this.getOptions(this.users, this.groups).map(option => option.value);
        this.setAvailabilityOptions(options);
      },

      deselectAllUsers() {
        this.setAvailabilityOptions([]);
      },

      displayFreePlanModal() {
        eventBus.$emit('free-plan-limit', { message: this.freePlanModalMessage });
      },

      setAvailabilityOptions(options) {
        this.$store.commit('checklist_builder/everybodyCanConduct', _.isEmpty(options));

        let { users, groups } = separateUsersAndGroups(options);

        users = filterListToEditableUsers(users).map(id => Number(id));
        groups = filterListToEditableGroups(groups).map(id => Number(id));

        this.$set(this.checklistRestrictions, 'users', users);
        this.$set(this.checklistRestrictions, 'groups', groups);

        !users.length && this.$set(this.checklistRestrictions, 'users', []);
        !groups.length && this.$set(this.checklistRestrictions, 'groups', []);

        if (!this.everybody) {
          this.$store.commit('checklist_builder/setChecklistRestrictions', this.checklistRestrictions);
        }
      },

      getOptions(users, groups) {
        users = users.map(user => `user_${user}`);
        groups = groups.map(group => `group_${group}`);

        return [...users, ...groups];
      },
    }
  };
</script>

<style scoped>
.availability > div {
  @apply py-2;
}
</style>
